import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { forkJoin } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS, Masks } from '@app/constants';
import { FeedbackService } from '@app/core/services';
import { VendorAddNewModel } from '@app/interfaces';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { FormComponent } from '@app/shared/forms/components';
import { UnitService } from '@app/shared/services';
import { UtilsService } from '@app/shared/services/utils.service';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import { VendorService } from '../../services/vendor.service';

@Component({
	selector: 'app-vendor-add-new',
	template: `
		<app-modal [onOkClick]="onSubmit" [okDisabled]="form.invalid">
			<sl-form [form]="form" [fields]="formlyFields" [model]="model" [options]="formlyFormOptions"></sl-form>
		</app-modal>
	`,
	standalone: true,
	imports: [ModalComponent, FormComponent],
})
export class VendorAddNewComponent implements OnInit {
	@ViewChild(FormComponent) formComponent: FormComponent;

	constructor(
		private readonly vendorService: VendorService,
		private readonly utilsService: UtilsService,
		private readonly unitService: UnitService,
		private readonly feedbackService: FeedbackService,
		private readonly changeDetectorRef: ChangeDetectorRef,
		public dialogRef: MatDialogRef<VendorAddNewComponent>
	) {}

	model: VendorAddNewModel = { Print1099: false, RemitZip: '' };
	formlyFormOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			lookups: {
				countryData: [],
			},
			selects: {
				states: [],
				countryStates: [],
				countries: [],
				tinTypes: [],
				vendor1099Boxes: [],
			},
		},
	};
	form = new UntypedFormGroup({});
	formlyFields: FormlyFieldConfig[] = [];

	ngOnInit(): void {
		this.formlyFields = this.getFormlyFields();
		this.loadDependencies();
	}

	private loadDependencies(): void {
		const states$ = this.utilsService.getStates();
		const tinTypes$ = this.utilsService.getTinTypes();
		const vendor1099Boxes$ = this.utilsService.getVendor1099Boxes();
		const nextVendorCode$ = this.unitService.getNextVendorCode();
		forkJoin({
			statesData: states$,
			tinTypesData: tinTypes$,
			vendor1099BoxesData: vendor1099Boxes$,
			nextVendorCodeData: nextVendorCode$,
		}).subscribe((results: any) => {
			const lookups = this.formlyFormOptions.formState.lookups;
			lookups.countryData = results.statesData.states;
			const selects = this.formlyFormOptions.formState.selects;

			selects.tinTypes = mapArrayToSelectOptions(
				results.tinTypesData.map((m: any) => ({ Id: m.Id, Description: m.Description })),
				{ value: 'Id', label: 'Description', all: false, sort: 'label' }
			);

			selects.vendor1099Boxes = mapArrayToSelectOptions(
				results.vendor1099BoxesData.map((m: any) => ({ Id: m.Id, Description: m.Name })),
				{ value: 'Id', label: 'Description', all: false, sort: 'label' }
			);

			selects.countries = mapArrayToSelectOptions(
				lookups.countryData.map((m: any) => ({ Id: m.CountryCode, Description: m.CountryDisplay })),
				{ value: 'Id', label: 'Description', all: false, sort: 'label' }
			);
			// Move US to the top of the list
			let usCountry = selects.countries.find((f: any) => f.value === 'US');
			const index = selects.countries.indexOf(usCountry);
			selects.countries.splice(index, 1);
			selects.countries.unshift(usCountry);

			// Set US States for Shipping
			usCountry = lookups.countryData.find((f: any) => f.CountryCode === 'US');
			selects.states = mapArrayToSelectOptions(
				usCountry.StateCodes.map((m: any) => ({ Id: m.StateCode, Description: m.StateDisplay })),
				{ value: 'Id', label: 'Description', all: false, sort: 'label' }
			);

			this.model.RemitCountry = 'US';
			this.onCountryChange(this.model.RemitCountry);

			// Set next vendor code
			this.model.ShortName = results.nextVendorCodeData.isValid ? results.nextVendorCodeData.data : null;
			const ctrlNameStartFocus = isNil(this.model.ShortName) ? 'ShortName' : 'VendorName';

			new Promise((resolve) => {
				this.changeDetectorRef.detectChanges();
				this.formlyFields = this.getFormlyFields();
				resolve(true);
			}).then(() => {
				this.formComponent.setFocus(ctrlNameStartFocus);
			});
		});
	}

	private onCountryChange(countryCode: string): void {
		const lookups = this.formlyFormOptions.formState.lookups;
		const states = lookups.countryData.find((f: any) => f.CountryCode === countryCode).StateCodes;
		const selects = this.formlyFormOptions.formState.selects;
		selects.countryStates = mapArrayToSelectOptions(
			states.map((m: any) => ({ Id: m.StateCode, Description: m.StateDisplay })),
			{ value: 'Id', label: 'Description', all: false, sort: 'label' }
		);
	}

	getFormlyFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: '',
				fieldGroup: [
					{
						fieldGroupClassName: 'col-xs-12 bootstrap-cols--narrow',
						fieldGroup: [
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										type: FORMLY_FIELD_TYPES.plainText,
										className: 'col-xs-12',
										templateOptions: {
											label: 'Fill in the required fields to add new vendor.',
										},
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'ShortName',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-3',
										templateOptions: {
											label: 'Vendor Code',
											required: true,
											maxLength: 20,
										},
										validators: {
											validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
										},
									},
									{
										key: 'VendorName',
										className: 'col-lg-9',
										type: FORMLY_FIELD_TYPES.input,
										templateOptions: {
											label: 'Vendor Name',
											required: true,
											maxLength: 50,
										},
										validators: {
											validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
										},
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'RemitAttn',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-6',
										templateOptions: {
											label: 'Attn To',
											maxLength: 50,
										},
									},
									{
										key: 'RemitCountry',
										type: FORMLY_FIELD_TYPES.select,
										className: 'col-lg-6',
										templateOptions: {
											label: 'Country',
											required: true,
											change: (field: FormlyFieldConfig) => {
												this.onCountryChange(field.formControl.value);
												const ctrlRemitState = field.form.get('RemitState');
												if (ctrlRemitState) {
													ctrlRemitState.setValue(null);
												}
											},
										},
										expressionProperties: {
											'templateOptions.options': 'formState.selects.countries',
										},
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'RemitAddress1',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-6',
										templateOptions: {
											label: 'Address 1',
											maxLength: 50,
										},
									},
									{
										key: 'RemitAddress2',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-6',
										templateOptions: {
											label: 'Address 2',
											maxLength: 50,
										},
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'FederalTaxID',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-3',
										templateOptions: {
											label: 'Tax ID',
											maxLength: 9,
										},
										expressionProperties: {
											'templateOptions.required': 'model.Print1099',
										},
										validators: {
											validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
										},
									},
									{
										key: 'RemitCity',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-5',
										templateOptions: {
											label: 'City',
											maxLength: 50,
										},
									},
									{
										key: 'RemitState',
										type: FORMLY_FIELD_TYPES.select,
										className: 'col-lg-2',
										templateOptions: {
											label: 'State',
										},
										expressionProperties: {
											'templateOptions.options': 'formState.selects.countryStates',
											'templateOptions.label': 'model.RemitCountry === "CA" ? "Province" : "State"',
										},
										hideExpression: (model: any): boolean => model?.RemitCountry === 'UK',
									},
									{
										key: 'RemitZip',
										type: FORMLY_FIELD_TYPES.maskedInput,
										className: 'col-lg-2',
										templateOptions: {
											label: 'Zip',
											maxLength: 10,
											maskString: Masks.Zip,
										},
										hideExpression: (model: any): boolean => model?.RemitCountry !== 'US',
									},
									{
										key: 'RemitZip',
										type: FORMLY_FIELD_TYPES.input,
										className: 'col-lg-2',
										templateOptions: {
											label: 'Postal Code',
											maxLength: 10,
										},
										hideExpression: (model: any): boolean => model?.RemitCountry === 'US',
									},
								],
							},
							{
								fieldGroupClassName: 'row',
								fieldGroup: [
									{
										key: 'Print1099',
										type: FORMLY_FIELD_TYPES.checkbox,
										className: 'col-lg-2',
										templateOptions: {
											label: 'Print 1099',
											appearance: 'standard',
										},
									},
									{
										key: 'Vendor1099BoxId',
										type: FORMLY_FIELD_TYPES.select,
										className: 'col-lg-7',
										templateOptions: {
											label: '1099 Box',
											required: true,
										},
										expressionProperties: {
											'templateOptions.options': 'formState.selects.vendor1099Boxes',
											'templateOptions.required': 'model.Print1099',
										},
										hideExpression: (model: any): boolean => !model.Print1099,
									},
									{
										key: 'TINId',
										type: FORMLY_FIELD_TYPES.select,
										className: 'col-lg-3',
										templateOptions: {
											label: 'Type of TIN',
										},
										expressionProperties: {
											'templateOptions.options': 'formState.selects.tinTypes',
											'templateOptions.required': 'model.Print1099',
										},
										hideExpression: (model: any): boolean => !model.Print1099,
									},
								],
							},
						],
					},
				],
			},
		];
	}

	onSubmit = (): void => {
		this.vendorService.addVendor(this.model).subscribe((response: any) => {
			if (response?.isValid) {
				this.dialogRef.close(response?.data);
			} else {
				this.feedbackService.error(response?.errors[0] || 'An error occurred while saving the vendor.');
			}
		});
	};
}
